import React, { useEffect, useState, useMemo } from 'react';
import './ScormAssign.css';
import { sortScorms, scormOptionLabel } from '../utils/scormHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faLock, faLockOpen, faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { showToast } from './Toast';

const ScormAssign = ({ onAssigned, show2FAOnly }) => {
  const [scorms, setScorms] = useState([]);
  const [scormSort, setScormSort] = useState('alpha');
  const [selectedScorm, setSelectedScorm] = useState('');
  const [message, setMessage] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupCounts, setGroupCounts] = useState({});
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // 2FA por grupo
  const [twoFAGroups, setTwoFAGroups] = useState([]);
  const [selected2FAGroup, setSelected2FAGroup] = useState('');
  const [twoFAMessage, setTwoFAMessage] = useState('');
  const [is2FALoading, setIs2FALoading] = useState(false);

  // CRUD Grupos
  const [adminGroups, setAdminGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [groupsLoading, setGroupsLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE || '';
  const getToken = () => sessionStorage.getItem('accessToken');

  const loadAdminGroups = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/groups`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      if (res.ok) { const d = await res.json(); setAdminGroups(d.groups || []); }
    } catch (e) {}
  };

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    setGroupsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/groups`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { showToast.error(d.error || 'Error'); return; }
      setNewGroupName('');
      // Actualizar estado local directamente (evita race condition con evento users:changed)
      setAdminGroups(prev => [...prev, { name, createdAt: new Date().toISOString() }]);
      showToast.success(`Grupo "${name}" creado correctamente`);
      // Notificar a otros componentes (ej. Admin.js) que los grupos cambiaron
      try { window.dispatchEvent(new CustomEvent('groups:changed', { detail: { action: 'create', name } })); } catch (e) {}
    } catch (e) { showToast.error('Error creando grupo'); }
    finally { setGroupsLoading(false); }
  };

  const renameGroup = async (oldName) => {
    const name = editGroupName.trim();
    if (!name || name === oldName) { setEditingGroup(null); return; }
    setGroupsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/groups`, { method: 'PUT', headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ oldName, newName: name }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { showToast.error(d.error || 'Error'); return; }
      setEditingGroup(null);
      // Actualizar estado local directamente
      setAdminGroups(prev => prev.map(g => g.name === oldName ? { ...g, name } : g));
      if (onAssigned) onAssigned({ silent: true });
      showToast.success(`Grupo renombrado a "${name}"`);
      // Notificar a otros componentes (ej. Admin.js) que los grupos cambiaron
      try { window.dispatchEvent(new CustomEvent('groups:changed', { detail: { action: 'rename', oldName, name } })); } catch (e) {}
    } catch (e) { showToast.error('Error renombrando grupo'); }
    finally { setGroupsLoading(false); }
  };

  const deleteGroup = async (name) => {
    const r = await Swal.fire({ title: 'Eliminar grupo', text: `¿Eliminar "${name}"? Los usuarios de este grupo quedarán sin grupo.`, icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar' });
    if (!r.isConfirmed) return;
    setGroupsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/groups`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); showToast.error(d.error || 'Error'); return; }
      // Actualizar estado local directamente
      setAdminGroups(prev => prev.filter(g => g.name !== name));
      if (onAssigned) onAssigned({ silent: true });
      showToast.success(`Grupo "${name}" eliminado`);
      // Notificar a otros componentes (ej. Admin.js) que los grupos cambiaron
      try { window.dispatchEvent(new CustomEvent('groups:changed', { detail: { action: 'delete', name } })); } catch (e) {}
    } catch (e) { alert('Error eliminando grupo'); }
    finally { setGroupsLoading(false); }
  };

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_BASE || '';

    const loadGroups = async () => {
      try {
        const token = sessionStorage.getItem('accessToken');
        const resUsers = await fetch(`${API_BASE}/get-all-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resUsers.ok) return;
        const du = await resUsers.json();
        const users = du.users || [];
        const counts = {};
        for (const u of users) {
          const g = u && u.grupo ? String(u.grupo) : '';
          if (!g) continue;
          counts[g] = (counts[g] || 0) + 1;
        }
        const grs = Object.keys(counts).sort((a, b) => a.localeCompare(b));
        setGroups(grs);
        setGroupCounts(counts);
      } catch (e) {
        // ignore
      }
    };

    const load = async () => {
      try {
        const token = sessionStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE}/admin/scorm/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('No autorizado');
        const data = await res.json();
        setScorms(data.scorms || []);
        await loadGroups();
        await loadAdminGroups();
        // Cargar estado 2FA por grupo
        try {
          const res2fa = await fetch(`${API_BASE}/admin/2fa/status`, { headers: { 'Authorization': `Bearer ${token}` } });
          if (res2fa.ok) { const d = await res2fa.json(); setTwoFAGroups(d.groups || []); }
        } catch (e) {}
      } catch (err) {
        setMessage('No se pudieron cargar cursos');
      }
    };
    load();
    const onUploaded = (e) => {
      // recargar la lista cuando hay un SCORM nuevo
      load();
    };
    const onUsersChanged = () => {
      // Solo recargar grupos derivados de usuarios (no adminGroups, que se maneja localmente)
      loadGroups();
    };
    window.addEventListener('scorm:uploaded', onUploaded);
    window.addEventListener('users:changed', onUsersChanged);
    return () => {
      window.removeEventListener('scorm:uploaded', onUploaded);
      window.removeEventListener('users:changed', onUsersChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canAct = Boolean(selectedScorm && selectedGroup) && !isAssigning && !isUnassigning;

  const toggle2FAGroup = async (enabled) => {
    if (!selected2FAGroup) return setTwoFAMessage('Selecciona un grupo');
    setIs2FALoading(true);
    setTwoFAMessage('');
    try {
      const token = sessionStorage.getItem('accessToken');
      const API_BASE = process.env.REACT_APP_API_BASE || '';
      const res = await fetch(`${API_BASE}/admin/2fa/config`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'group', grupo: selected2FAGroup, enabled })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error');
      setTwoFAMessage(data.message || 'Actualizado');
      // Refrescar estado
      const res2 = await fetch(`${API_BASE}/admin/2fa/status`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res2.ok) { const d = await res2.json(); setTwoFAGroups(d.groups || []); }
      // Notificar a la tabla de usuarios para que refresque
      try { window.dispatchEvent(new CustomEvent('users:changed', { detail: { reason: '2fa-group' } })); } catch (e) {}
    } catch (err) {
      setTwoFAMessage(err.message || 'Error');
    } finally {
      setIs2FALoading(false);
    }
  };

  const assignToGroup = async () => {
    if (!selectedScorm || !selectedGroup) return setMessage('Seleccione SCORM y Grupo');
    setIsAssigning(true);
    setMessage('');
    try {
      const token = sessionStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/admin/scorm/assign-group`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scormId: selectedScorm, group: selectedGroup })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error asignando grupo');
      setMessage(`Asignado a ${data.usersAffected || 0} usuarios`);
      if (onAssigned) setTimeout(() => onAssigned(), 600);
    } catch (err) {
      setMessage(err.message || 'Error');
    } finally {
      setIsAssigning(false);
    }
  };

  const unassignFromGroup = async () => {
    if (!selectedScorm || !selectedGroup) return setMessage('Seleccione SCORM y Grupo');
    const r = await Swal.fire({ title: 'Desasignar SCORM', text: '¿Desasignar este SCORM del grupo seleccionado?', icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, desasignar', cancelButtonText: 'Cancelar' });
    if (!r.isConfirmed) return;
    setIsUnassigning(true);
    setMessage('');
    try {
      const token = sessionStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/admin/scorm/unassign-group`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scormId: selectedScorm, group: selectedGroup })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error desasignando grupo');
      setMessage(`Desasignado en ${data.usersAffected || 0} usuarios`);
      if (onAssigned) setTimeout(() => onAssigned(), 600);
    } catch (err) {
      setMessage(err.message || 'Error');
    } finally {
      setIsUnassigning(false);
    }
  };

  // Merge: adminGroups (fuente de verdad de grupos creados) + groups (derivados de usuarios)
  // Así un grupo recién creado aparece inmediatamente aunque no tenga usuarios
  const allGroups = useMemo(() => {
    const set = new Set(groups);
    for (const g of adminGroups) {
      if (g && g.name) set.add(g.name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [groups, adminGroups]);

  return (
	<>
	{!show2FAOnly && (<>
	<div className="scorm-admin-card col-12 text-left">
		<div className="scorm-card">
			<div className="scorm-card__header">
				<div className="scorm-card__title">Asignar a Grupo</div>
				<div className="scorm-card__subtitle">Asignación masiva por grupo</div>
			</div>
			<div className="scorm-card__body">
				<div className="scorm-row scorm-row--1">
					<div className="scorm-field">
						<label>Curso SCORM</label>
						<div className="scorm-sort-toggle">
							<button type="button" className={`scorm-sort-btn ${scormSort === 'alpha' ? 'scorm-sort-btn--active' : ''}`} onClick={() => setScormSort('alpha')}>A-Z</button>
							<button type="button" className={`scorm-sort-btn ${scormSort === 'recent' ? 'scorm-sort-btn--active' : ''}`} onClick={() => setScormSort('recent')}>Recientes</button>
						</div>
						<select className="scorm-select" value={selectedScorm} onChange={e => setSelectedScorm(e.target.value)}>
							<option value="">-- Seleccionar SCORM --</option>
							{sortScorms(scorms, scormSort).map(s => (
								<option key={s.id} value={s.id}>{scormOptionLabel(s)}</option>
							))}
						</select>
						<div className="scorm-meta">{scorms.length ? `${scorms.length} cursos disponibles` : 'Sin cursos cargados'}</div>
					</div>
					<div className="scorm-field">
						<label>Grupo</label>
						<select className="scorm-select" value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}>
							<option value="">-- Seleccionar Grupo --</option>
							{allGroups.map(g => (
								<option key={g} value={g}>{g}{groupCounts[g] ? ` (${groupCounts[g]})` : ''}</option>
							))}
						</select>
						<div className="scorm-meta">{selectedGroup ? `Usuarios en grupo: ${groupCounts[selectedGroup] || 0}` : 'Selecciona un grupo para ver usuarios'}</div>
					</div>
				</div>

				<div className="scorm-actions">
          <button type="button" className="scorm-btn scorm-btn--primary" onClick={assignToGroup} disabled={!canAct}>
						{isAssigning ? 'Asignando...' : 'Asignar'}
					</button>
          <button type="button" className="scorm-btn scorm-btn--danger" onClick={unassignFromGroup} disabled={!canAct}>
						{isUnassigning ? 'Desasignando...' : 'Desasignar'}
					</button>
				</div>

				{message && (
					<div className={`scorm-message ${String(message).toLowerCase().includes('error') ? 'scorm-message--error' : ''}`}>{message}</div>
				)}
			</div>
		</div>
	</div>

	{/* Gestión de Grupos */}
	<div className="scorm-admin-card col-12 text-left" style={{ marginTop: 16 }}>
		<div className="scorm-card">
			<div className="scorm-card__header">
				<div className="scorm-card__title">Gestión de Grupos</div>
				<div className="scorm-card__subtitle">Crear, renombrar y eliminar grupos</div>
			</div>
			<div className="scorm-card__body">
				<div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
					<input
						type="text"
						className="scorm-input"
						value={newGroupName}
						onChange={(e) => setNewGroupName(e.target.value)}
						placeholder="Nombre del nuevo grupo"
						onKeyDown={(e) => { if (e.key === 'Enter') createGroup(); }}
						disabled={groupsLoading}
						style={{ maxWidth: 300 }}
					/>
					<button type="button" className="scorm-btn scorm-btn--primary" onClick={createGroup} disabled={groupsLoading || !newGroupName.trim()}>
						<FontAwesomeIcon icon={faPlus} /> Crear
					</button>
				</div>
				{adminGroups.length === 0 ? (
					<div className="scorm-meta">No hay grupos creados. Crea uno arriba.</div>
				) : (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
						{adminGroups.map((g) => (
							<div key={g.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid rgba(0,0,0,0.04)' }}>
								{editingGroup === g.name ? (
									<>
										<input type="text" className="scorm-input" value={editGroupName} onChange={(e) => setEditGroupName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') renameGroup(g.name); if (e.key === 'Escape') setEditingGroup(null); }} autoFocus style={{ maxWidth: 200, padding: '6px 10px' }} />
										<button type="button" className="scorm-btn scorm-btn--primary" style={{ padding: '6px 10px' }} onClick={() => renameGroup(g.name)} disabled={groupsLoading}>Guardar</button>
										<button type="button" className="scorm-btn scorm-btn--secondary" style={{ padding: '6px 10px' }} onClick={() => setEditingGroup(null)}>Cancelar</button>
									</>
								) : (
									<>
										<span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{g.name}</span>
										<button type="button" className="scorm-btn scorm-btn--secondary" style={{ padding: '4px 10px' }} onClick={() => { setEditingGroup(g.name); setEditGroupName(g.name); }}><FontAwesomeIcon icon={faEdit} /></button>
										<button type="button" className="scorm-btn scorm-btn--danger" style={{ padding: '4px 10px' }} onClick={() => deleteGroup(g.name)} disabled={groupsLoading}><FontAwesomeIcon icon={faTrash} /></button>
									</>
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	</div>
	</>)}

	{show2FAOnly && (
	<div className="scorm-admin-card col-12 text-left">
		<div className="scorm-card">
			<div className="scorm-card__header">
				<div className="scorm-card__title">2FA por Grupo</div>
				<div className="scorm-card__subtitle">Verificación por correo al iniciar sesión</div>
			</div>
			<div className="scorm-card__body">
				<div className="scorm-field">
					<label>Grupo</label>
					<select className="scorm-select" value={selected2FAGroup} onChange={e => setSelected2FAGroup(e.target.value)}>
						<option value="">-- Seleccionar Grupo --</option>
						{allGroups.map(g => (
							<option key={g} value={g}>{g}{groupCounts[g] ? ` (${groupCounts[g]})` : ''} {twoFAGroups.includes(g) ? '✓ 2FA' : ''}</option>
						))}
					</select>
					<div className="scorm-meta">
						{selected2FAGroup
							? (twoFAGroups.includes(selected2FAGroup) ? '2FA activo para este grupo' : '2FA inactivo para este grupo')
							: 'Selecciona un grupo'}
					</div>
				</div>

				<div className="scorm-actions">
					<button type="button" className="scorm-btn scorm-btn--primary" onClick={() => toggle2FAGroup(true)} disabled={!selected2FAGroup || is2FALoading}>
						<FontAwesomeIcon icon={faLock} /> {is2FALoading ? '...' : 'Activar 2FA'}
					</button>
					<button type="button" className="scorm-btn scorm-btn--secondary" onClick={() => toggle2FAGroup(false)} disabled={!selected2FAGroup || is2FALoading}>
						<FontAwesomeIcon icon={faLockOpen} /> {is2FALoading ? '...' : 'Desactivar 2FA'}
					</button>
				</div>

				{twoFAGroups.length > 0 && (
					<div style={{ marginTop: 12, fontSize: 13, color: '#555' }}>
						<strong>Grupos con 2FA activo:</strong>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
							{twoFAGroups.map(g => (
								<span key={g} style={{ background: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>
									<FontAwesomeIcon icon={faShieldAlt} /> {g}
								</span>
							))}
						</div>
					</div>
				)}

				{twoFAMessage && (
					<div className={`scorm-message ${String(twoFAMessage).toLowerCase().includes('error') ? 'scorm-message--error' : ''}`}>{twoFAMessage}</div>
				)}
			</div>
		</div>
	</div>
	)}
	</>
  );
};

export default ScormAssign;
